import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import ExpertLayout from '../../components/ExpertLayout';
import PageHeader from '../../components/expert/PageHeader';
import { expertDashboardApi } from '../../lib/http';
import WeeklyScheduleTab from '../../components/expert/working-hours/WeeklyScheduleTab';
import SeasonalSchedulesTab from '../../components/expert/working-hours/SeasonalSchedulesTab';
import ExceptionsTab from '../../components/expert/working-hours/ExceptionsTab';

type TabType = 'weekly' | 'seasonal' | 'exceptions';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ReactNode;
}

const ALL_TABS: TabConfig[] = [
  {
    id: 'weekly',
    label: 'Weekly Schedule',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'seasonal',
    label: 'Seasonal',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
  {
    id: 'exceptions',
    label: 'Exceptions',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    ),
  },
];

const WorkingHours: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('weekly');

  // Fetch expert profile to check use_seasonal_schedules
  const { data: profileData } = useQuery({
    queryKey: ['expert-profile'],
    queryFn: async () => {
      const response = await expertDashboardApi.profile.get();
      return response.data;
    },
  });

  // Fetch schedules
  const { data: schedulesData, isLoading: schedulesLoading } = useQuery({
    queryKey: ['expert-schedules'],
    queryFn: async () => {
      const response = await expertDashboardApi.schedules.list();
      return response.data;
    },
  });

  // Fetch schedule exceptions
  const { data: exceptionsData, isLoading: exceptionsLoading } = useQuery({
    queryKey: ['schedule-exceptions'],
    queryFn: async () => {
      const response = await expertDashboardApi.scheduleExceptions.list();
      return response.data;
    },
  });

  const useSeasonalSchedules = profileData?.use_seasonal_schedules ?? false;

  // Filter tabs based on use_seasonal_schedules setting
  const visibleTabs = useMemo(() => {
    if (useSeasonalSchedules) {
      return ALL_TABS;
    }
    return ALL_TABS.filter(tab => tab.id !== 'seasonal');
  }, [useSeasonalSchedules]);

  // Reset to weekly tab if seasonal is disabled while on that tab
  useEffect(() => {
    if (!useSeasonalSchedules && activeTab === 'seasonal') {
      setActiveTab('weekly');
    }
  }, [useSeasonalSchedules, activeTab]);

  const schedules = schedulesData?.schedules || [];
  const exceptions = exceptionsData?.exceptions || [];
  const defaultSchedule = schedules.find(s => s.is_default);

  return (
    <ExpertLayout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <PageHeader
          title="Working Hours"
          backTo="/app"
        />

        {/* Timezone indicator - hidden on very small screens */}
        <div className="mb-3 sm:mb-6 hidden xs:flex items-center gap-2 text-xs sm:text-sm text-gray-500">
          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="truncate">Times in your local timezone</span>
        </div>

        {/* Tab Navigation - Compact pill style for mobile */}
        <div className="mb-4 sm:mb-6">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 flex items-center justify-center gap-1.5 py-2 px-2 sm:px-3 rounded-lg font-medium text-xs sm:text-sm
                  transition-all duration-200
                  ${activeTab === tab.id
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                <span className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${activeTab === tab.id ? 'text-purple-500' : 'text-gray-400'}`}>
                  {tab.icon}
                </span>
                <span className="hidden min-[400px]:inline truncate">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-3 sm:mt-6">
          {activeTab === 'weekly' && (
            <WeeklyScheduleTab
              schedule={defaultSchedule}
              isLoading={schedulesLoading}
            />
          )}
          {activeTab === 'seasonal' && useSeasonalSchedules && (
            <SeasonalSchedulesTab
              schedules={schedules.filter(s => !s.is_default)}
              defaultSchedule={defaultSchedule}
              isLoading={schedulesLoading}
            />
          )}
          {activeTab === 'exceptions' && (
            <ExceptionsTab
              exceptions={exceptions}
              isLoading={exceptionsLoading}
            />
          )}
        </div>
      </div>
    </ExpertLayout>
  );
};

export default WorkingHours;
